import React, { useState, useEffect, useRef } from 'react';
import { uploadImageToCloudinaryWithProgress } from '../../../../api/external/cloudinary/upload';

import EditModalShell from '../forms/EditModalShell/EditModalShell';
import LoadingModal from '../LoadingModal/LoadingModal';
import AdminProgressBar from '../../AdminProgressBar/AdminProgressBar';
import ImageInputWithPreview from '../forms/ImageInputWithPreview/ImageInputWithPreview';
import FormField from '../forms/FormField/FormField';

import './ServiceModal.css';

const ServiceModal = ({ onClose, onSubmit, service, mode }) => {
  const initialFormState = {
    // fields for Services Page
    title: '',
    description: '',
    imageUrl: '', // image path
    imagePublicId: '',
    janeUrl: '',
    order: 0,
    showOnServices: false,

    // fields for Home Page
    homeTitle: '',
    homeDescription: '',
    homeLabel: '',
    homeImageUrl: '', // image path
    homeImagePublicId: '',
    homeJaneUrl: '',
    showOnHome: false,
  };

  const [formData, setFormData] = useState(initialFormState);
  const [selectedFiles, setSelectedFiles] = useState({}); // raw files
  const [imagePreviewUrls, setImagePreviewUrls] = useState({});
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const {
    title,
    description,
    imageUrl,
    janeUrl,
    order,
    showOnServices,
    homeTitle,
    homeDescription,
    homeLabel,
    homeImageUrl,
    homeJaneUrl,
    showOnHome,
  } = formData;

  const firstInputRef = useRef();

  useEffect(() => {
    setFormData({
      title: service?.title || '',
      description: service?.description || '',
      imageUrl: service?.imageUrl || '',
      imagePublicId: service?.imagePublicId || '',
      janeUrl: service?.janeUrl || '',
      order: service?.order || 0,
      showOnServices: mode === 'edit' ? service?.showOnServices || false : true,

      homeTitle: service?.homeTitle || '',
      homeDescription: service?.homeDescription || '',
      homeLabel: service?.homeLabel || '',
      homeImageUrl: service?.homeImageUrl || '',
      homeImagePublicId: service?.homeImagePublicId || '',
      homeJaneUrl: service?.homeJaneUrl || '',
      showOnHome: mode === 'edit' ? service?.showOnHome || false : true,
    });

    setSelectedFiles({});
    setImagePreviewUrls({
      imageUrl: service?.imageUrl || null,
      homeImageUrl: service?.homeImageUrl || null,
    });

    firstInputRef.current?.focus();
  }, [mode, service]);

  const handleInputChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCheckboxChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.checked }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];
    if (!file) return;

    setSelectedFiles((prev) => ({ ...prev, [name]: file }));
    setImagePreviewUrls((prev) => ({
      ...prev,
      [name]: URL.createObjectURL(file),
    }));
  };

  const handleRemoveImage = (name) => {
    if (!['imageUrl', 'homeImageUrl'].includes(name)) return;

    const publicIdField =
      name === 'imageUrl' ? 'imagePublicId' : 'homeImagePublicId';

    setFormData((prev) => ({
      ...prev,
      [name]: '',
      [publicIdField]: '',
    }));

    setSelectedFiles((prev) => ({ ...prev, [name]: null }));
    setImagePreviewUrls((prev) => ({ ...prev, [name]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const hasUpload = selectedFiles?.imageUrl || selectedFiles?.homeImageUrl;

    setLoading(hasUpload);
    setProgress(0);

    try {
      const finalData = { ...formData };

      if (selectedFiles?.imageUrl) {
        const res = await uploadImageToCloudinaryWithProgress(
          selectedFiles.imageUrl,
          setProgress,
          'services/services',
          [0, 50],
        );

        finalData.imageUrl = res.url;
        finalData.imagePublicId = res.publicId;
      }

      if (selectedFiles?.homeImageUrl) {
        const res = await uploadImageToCloudinaryWithProgress(
          selectedFiles.homeImageUrl,
          setProgress,
          'services/home',
          [50, 100],
        );

        finalData.homeImageUrl = res.url;
        finalData.homeImagePublicId = res.publicId;
      }

      setProgress(100);

      await onSubmit(finalData);
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Upload failed.');
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  return (
    <EditModalShell
      formId='service-modal-form'
      onClose={onClose}
      onSubmit={handleSubmit}
      mode={mode}
      isLoading={loading}
      itemLabel='Service'
    >
      {loading && (
        <LoadingModal>
          <AdminProgressBar progress={progress} />
        </LoadingModal>
      )}

      <div className='service-modal-pages-container'>
        {/* Services Page */}
        <div className='service-modal-services-page-container'>
          <h4 className='service-model-pages-title'>Services Page</h4>

          <ImageInputWithPreview
            name='imageUrl'
            imagePreviewUrl={imagePreviewUrls?.imageUrl}
            imageUrl={imageUrl}
            selectedImgAlt='Selected Service Image Preview'
            currentImgAlt='Current Service'
            onFileChange={handleFileChange}
            onRemoveImage={handleRemoveImage}
          />

          <FormField
            type='number'
            name='order'
            label='Order'
            value={order}
            onChange={handleInputChange}
            required
          />

          <FormField
            type='text'
            name='title'
            label='Title'
            value={title}
            onChange={handleInputChange}
            ref={firstInputRef}
            required
          />

          <FormField
            type='textarea'
            name='description'
            label='Description'
            value={description}
            onChange={handleInputChange}
          />

          <FormField
            type='url'
            name='janeUrl'
            label='Booking Link'
            autoComplete='on'
            value={janeUrl}
            onChange={handleInputChange}
          />

          <FormField
            type='checkbox'
            name='showOnServices'
            label='Show on Services Page'
            value={showOnServices}
            onChange={handleCheckboxChange}
          />
        </div>

        {/* Home Page */}
        <div className='service-modal-home-page-container'>
          <h4 className='service-model-pages-title'>Home Page</h4>

          <ImageInputWithPreview
            name='homeImageUrl'
            imagePreviewUrl={imagePreviewUrls?.homeImageUrl}
            imageUrl={homeImageUrl}
            selectedImgAlt='Selected Home Image Preview'
            currentImgAlt='Current Service'
            noImgMsg='Upload a photo if different from Services Page'
            onFileChange={handleFileChange}
            onRemoveImage={handleRemoveImage}
          />

          <FormField
            type='text'
            name='homeTitle'
            label={'Description Title'}
            value={homeTitle}
            onChange={handleInputChange}
          />

          <FormField
            type='textarea'
            name='homeDescription'
            label='Description'
            value={homeDescription}
            onChange={handleInputChange}
          />

          <FormField
            type='url'
            name='homeJaneUrl'
            label='Booking Link'
            autoComplete='on'
            value={homeJaneUrl}
            onChange={handleInputChange}
          />

          <FormField
            type='text'
            name='homeLabel'
            label='Label'
            value={homeLabel}
            onChange={handleInputChange}
          />

          <FormField
            type='checkbox'
            name='showOnHome'
            label='Show on Home Page'
            value={showOnHome}
            onChange={handleCheckboxChange}
          />
        </div>
      </div>
    </EditModalShell>
  );
};

export default ServiceModal;
